import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../../store/useStore";
import { buildAssetUrl } from "../../../utils/api";
import { fetchAndDownloadInvoice } from "../../../utils/invoiceGenerator";
import { ORDER_STATUSES } from "../../TrackOrder/trackOrderConstants";
import CancelModal from "./OrdersSection/CancelModal";
import {
  FaTruck,
  FaBolt,
  FaCalendarAlt,
  FaPhoneAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaBoxOpen,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
  FaMotorcycle,
  FaFileInvoice,
  FaStore,
  FaCheck,
  FaSearch,
  FaCopy,
  FaMapMarkerAlt,
  FaCreditCard,
} from "react-icons/fa";
import { MdMyLocation } from "react-icons/md";

// ── Status Milestone Mapping (fallback when backend timeline timestamps are sparse) ──
const STATUS_MILESTONE_STEP = {
  PENDING: 1,
  ACCEPTED: 2,
  CONFIRMED: 2,
  PROCESSING: 3,
  PACKED: 3,
  DISPATCHED: 4,
  SHIPPED: 5,
  OUT_FOR_DELIVERY: 5,
  DELIVERED: 6,
  CANCELLED: 0,
};

const STATUS_PROGRESS_PERCENTAGE = {
  PENDING: 15,
  ACCEPTED: 35,
  CONFIRMED: 35,
  PROCESSING: 55,
  PACKED: 60,
  DISPATCHED: 70,
  SHIPPED: 85,
  OUT_FOR_DELIVERY: 90,
  DELIVERED: 100,
  CANCELLED: 0,
};

const STATUS_DESCRIPTION_TEXT = {
  PENDING: "Order received and awaiting store confirmation",
  ACCEPTED: "Order confirmed by the store",
  CONFIRMED: "Order confirmed and being prepared",
  PROCESSING: "Items are being packed and quality checked",
  PACKED: "Order packed and waiting for pickup",
  DISPATCHED: "Order dispatched from store",
  SHIPPED: "Delivery partner is on the way to your address",
  OUT_FOR_DELIVERY: "Delivery partner is nearby with your order",
  DELIVERED: "Order successfully delivered! Enjoy your fresh items",
  CANCELLED: "This order was cancelled",
};

// ── 6 Timeline Steps Definition ─────────────────────────────────────────
const TRACKING_STEPS = [
  {
    stepNum: 1,
    key: "order_placed_at",
    altKeys: ["placed_at", "created_at"],
    label: "Order Placed",
    icon: FaBoxOpen,
    desc: "Your order has been received",
  },
  {
    stepNum: 2,
    key: "order_confirmed_at",
    altKeys: ["confirmed_at", "accepted_at"],
    label: "Order Confirmed",
    icon: FaStore,
    desc: "Order confirmed by store",
  },
  {
    stepNum: 3,
    key: "order_packed_at",
    altKeys: ["packed_at", "order_assigned_at", "assigned_at"],
    label: "Being Prepared",
    icon: FaBoxOpen,
    desc: "Items picked and packed fresh",
  },
  {
    stepNum: 4,
    key: "order_dispatched_at",
    altKeys: ["dispatched_at"],
    label: "Dispatched",
    icon: FaTruck,
    desc: "Order dispatched from hub",
  },
  {
    stepNum: 5,
    key: "out_for_delivery_at",
    altKeys: ["shipped_at"],
    label: "Out for Delivery",
    icon: MdMyLocation,
    desc: "Delivery partner is heading to you",
  },
  {
    stepNum: 6,
    key: "delivered_at",
    altKeys: ["completed_at"],
    label: "Delivered",
    icon: FaCheckCircle,
    desc: "Order delivered safely",
  },
];

// ── Helper formatting ──────────────────────────────────────────────
const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtRs = (n) => `₹${Number(n || 0).toFixed(2)}`;

const STATUS_PILL = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CONFIRMED: "bg-blue-50 text-blue-700 border-blue-200",
  PROCESSING: "bg-purple-50 text-purple-700 border-purple-200",
  PACKED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  DISPATCHED: "bg-cyan-50 text-cyan-700 border-cyan-200",
  SHIPPED: "bg-teal-50 text-teal-700 border-teal-200",
  OUT_FOR_DELIVERY: "bg-emerald-50 text-emerald-800 border-emerald-300",
  DELIVERED: "bg-emerald-100 text-[#046a38] border-emerald-300",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_DOT = {
  PENDING: "bg-amber-500",
  ACCEPTED: "bg-emerald-500",
  CONFIRMED: "bg-blue-500",
  PROCESSING: "bg-purple-500",
  PACKED: "bg-indigo-500",
  DISPATCHED: "bg-cyan-500",
  SHIPPED: "bg-teal-500",
  OUT_FOR_DELIVERY: "bg-emerald-600 animate-pulse",
  DELIVERED: "bg-[#046a38]",
  CANCELLED: "bg-rose-500",
};

// ── Left Sidebar: Order List Item ──────────────────────────────────
const OrderListItem = ({ order, isSelected, onClick }) => {
  const id = order.order_id ?? order.id;
  const rawStatus = (order.order_status ?? order.status ?? "PENDING").toUpperCase();
  const statusLabel = ORDER_STATUSES[rawStatus]?.label ?? rawStatus;
  const pillClass = STATUS_PILL[rawStatus] ?? "bg-gray-50 text-gray-600 border-gray-200";
  const dotClass = STATUS_DOT[rawStatus] ?? "bg-gray-400";
  const items = order.items ?? [];
  const total = Number(order.total ?? order.total_amount ?? 0);

  const thumbs = items.slice(0, 3);
  const extra = items.length - 3;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full text-left transition-all duration-200 p-3.5 border-b border-gray-100/80 last:border-b-0 ${
        isSelected
          ? "bg-gradient-to-r from-emerald-50/90 to-emerald-50/30 border-l-4 border-l-[#046a38] shadow-2xs"
          : "bg-white hover:bg-gray-50/90 border-l-4 border-l-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-mono text-xs font-black tracking-wide text-gray-900 truncate">
          {order.order_number ?? `#${id}`}
        </span>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-extrabold ${pillClass}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
          {statusLabel}
        </span>
      </div>

      {/* Product Thumbnails preview */}
      {items.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center">
            {thumbs.map((item, idx) => {
              const raw = item.image ?? item.product_image ?? item.product?.image ?? null;
              const url = buildAssetUrl(raw);
              return (
                <div
                  key={idx}
                  className="h-7 w-7 shrink-0 rounded-lg overflow-hidden border border-white bg-emerald-50 shadow-2xs"
                  style={{ marginLeft: idx > 0 ? -6 : 0, zIndex: 4 - idx, position: "relative" }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gray-100">
                      <FaBoxOpen className="h-3 w-3 text-emerald-400" />
                    </div>
                  )}
                </div>
              );
            })}
            {extra > 0 && (
              <div
                className="h-7 w-7 shrink-0 rounded-lg border border-white bg-gray-100 flex items-center justify-center shadow-2xs"
                style={{ marginLeft: -6, position: "relative" }}
              >
                <span className="text-[9px] font-black text-gray-600">+{extra}</span>
              </div>
            )}
          </div>
          <span className="text-[11px] text-gray-500 font-medium">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Date and Amount */}
      <div className="flex items-center justify-between text-xs pt-0.5">
        <span className="text-[11px] text-gray-400">
          {formatDate(order.placed_at ?? order.created_at)}
        </span>
        <span className="font-extrabold text-gray-900">
          {fmtRs(total)}
        </span>
      </div>
    </button>
  );
};

// ── Timeline Step Row ──────────────────────────────────────────────
const TimelineStep = ({ step, isDone, isActive, isLast }) => {
  const Icon = step.icon;

  return (
    <div className="flex gap-4 group">
      {/* Connector column */}
      <div className="flex flex-col items-center">
        <div
          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border-2 transition-all duration-300 ${
            isDone
              ? "border-[#046a38] bg-[#046a38] text-white shadow-md shadow-emerald-200"
              : isActive
              ? "border-[#046a38] bg-white text-[#046a38] ring-4 ring-emerald-100 shadow-md shadow-emerald-100 animate-pulse"
              : "border-gray-200 bg-gray-50 text-gray-300"
          }`}
        >
          {isDone ? (
            <FaCheck className="h-4 w-4" />
          ) : (
            <Icon className="h-4 w-4" />
          )}

          {isActive && (
            <span className="absolute -right-1 -top-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#046a38]" />
            </span>
          )}
        </div>

        {!isLast && (
          <div
            className={`mt-1.5 w-0.5 flex-1 rounded-full transition-all duration-500 ${
              isDone ? "bg-[#046a38]" : "bg-gray-200"
            }`}
            style={{ minHeight: 32 }}
          />
        )}
      </div>

      {/* Step description */}
      <div className="pb-6 pt-1 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className={`text-sm font-bold tracking-tight ${
              isDone
                ? "text-gray-900"
                : isActive
                ? "text-[#046a38] font-extrabold"
                : "text-gray-400"
            }`}
          >
            {step.label}
          </p>

          {isDone && (
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-[#046a38]">
              Completed
            </span>
          )}

          {isActive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[10px] font-extrabold text-[#046a38]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#046a38] animate-ping" />
              In Progress
            </span>
          )}
        </div>

        {step.timestamp ? (
          <p className="mt-1 text-xs font-semibold text-[#046a38]">
            {fmtDateTime(step.timestamp)}
          </p>
        ) : isActive ? (
          <p className="mt-1 text-xs font-medium text-emerald-700">
            {step.desc} · <span className="italic font-semibold">Updating live...</span>
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-gray-400 font-normal">
            {step.desc}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main TrackOrderSection Component ───────────────────────────────
const TrackOrderSection = () => {
  const { orderId: orderIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdFromUrl = orderIdParam || searchParams.get("orderId");

  // Zustand Store
  const orders = useStore((s) => s.orders);
  const orders_loading = useStore((s) => s.orders_loading);
  const orders_error = useStore((s) => s.orders_error);
  const orders_pagination = useStore((s) => s.orders_pagination);
  const fetchOrders = useStore((s) => s.fetchOrders);
  const getOrderDetails = useStore((s) => s.getOrderDetails);
  const cancelOrder = useStore((s) => s.cancelOrder);

  const order_tracking = useStore((s) => s.order_tracking);
  const tracking_loading = useStore((s) => s.tracking_loading);
  const fetchOrderTracking = useStore((s) => s.fetchOrderTracking);

  // Local State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingSelectedOrder, setLoadingSelectedOrder] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterQuery, setFilterQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const PAGE_LIMIT = 10;
  const selectedOrderId = selectedOrder?.order_id ?? selectedOrder?.id;
  const selectedOrderIdRef = useRef(selectedOrderId);
  const detailsRef = useRef(null);

  useEffect(() => {
    selectedOrderIdRef.current = selectedOrderId;
  }, [selectedOrderId]);

  // ── 1. Fetch Orders List On Mount & Page Change ──
  useEffect(() => {
    fetchOrders({ page: currentPage, limit: PAGE_LIMIT });
  }, [currentPage]); // eslint-disable-line

  // ── 2. Handle URL orderId synchronization & fetch exact details ──
  useEffect(() => {
    let isCancelled = false;

    const syncTargetOrder = async () => {
      const targetId = orderIdFromUrl ? String(orderIdFromUrl).trim() : null;

      if (targetId) {
        const cleanTargetId = targetId.replace(/^#/, "").trim();
        const targetIdLower = targetId.toLowerCase();
        const cleanTargetIdLower = cleanTargetId.toLowerCase();

        // First check if already in loaded orders by ID or order number
        const found = orders.find((o) => {
          const oId = String(o.order_id ?? o.id ?? "").toLowerCase();
          const oNum = String(o.order_number ?? "").replace(/^#/, "").toLowerCase();
          const oNumRaw = String(o.order_number ?? "").toLowerCase();
          return (
            oId === targetIdLower ||
            oId === cleanTargetIdLower ||
            oNum === cleanTargetIdLower ||
            oNumRaw === targetIdLower
          );
        });

        if (found) {
          setSelectedOrder(found);
        }

        const resolvedId = found ? (found.order_id ?? found.id) : cleanTargetId;

        // Fetch deep order details directly to ensure items, addresses and status are complete
        setLoadingSelectedOrder((prev) => (!found ? true : prev));
        try {
          const detailed = await getOrderDetails(resolvedId);
          if (!isCancelled && detailed) {
            setSelectedOrder((prev) => ({ ...(prev || {}), ...detailed }));
          }
        } catch (err) {
          console.error("Failed to load order details for", resolvedId, err);
        } finally {
          if (!isCancelled) setLoadingSelectedOrder(false);
        }

        // Fetch live tracking
        fetchOrderTracking(resolvedId);

        // On mobile screens, scroll the tracking details into view smoothly
        if (window.innerWidth < 1024) {
          setTimeout(() => {
            detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 150);
        }
        return;
      }

      // If no orderId in URL, select first available order
      if (!selectedOrder && orders.length > 0) {
        const first = orders[0];
        setSelectedOrder(first);
        const firstId = first.order_id ?? first.id;
        if (firstId) {
          setSearchParams({ orderId: String(firstId) }, { replace: true });
          fetchOrderTracking(firstId);
        }
      }
    };

    syncTargetOrder();

    return () => {
      isCancelled = true;
    };
  }, [orderIdFromUrl, orders, getOrderDetails, fetchOrderTracking]); // eslint-disable-line

  // ── 3. Auto-sync polling every 12s for live tracking ──
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== "visible") return;
      if (selectedOrderIdRef.current) {
        fetchOrderTracking(selectedOrderIdRef.current, { silent: true });
      }
      fetchOrders({ page: 1, limit: PAGE_LIMIT, silent: true });
    };

    const intervalId = setInterval(sync, 12000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") sync();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchOrders, fetchOrderTracking]);

  // ── User Selection Handler ──
  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    const id = order.order_id ?? order.id;
    if (id) {
      setSearchParams({ orderId: String(id) });
      fetchOrderTracking(id);
      if (window.innerWidth < 1024) {
        setTimeout(() => {
          detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  };

  // ── Manual Refresh ──
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchOrders({ page: currentPage, limit: PAGE_LIMIT, silent: true }),
        selectedOrderId ? fetchOrderTracking(selectedOrderId) : Promise.resolve(),
        selectedOrderId ? getOrderDetails(selectedOrderId) : Promise.resolve(),
      ]);
      toast.success("Tracking updated");
    } catch {
      toast.error("Could not refresh live tracking");
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Cancel Order Handler ──
  const handleCancelConfirm = async (remarks) => {
    if (!selectedOrder) return;
    const orderId = selectedOrder.order_id ?? selectedOrder.id;
    setCancelling(true);
    try {
      await cancelOrder(orderId, remarks);
      toast.success("Order cancelled successfully");
      setShowCancelModal(false);
      setSelectedOrder((prev) =>
        prev ? { ...prev, status: "CANCELLED", order_status: "CANCELLED" } : prev
      );
      if (orderId) fetchOrderTracking(orderId);
    } catch (err) {
      toast.error(err.message || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  // ── Download Invoice ──
  const handleDownloadInvoice = async () => {
    if (!selectedOrder || downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      await fetchAndDownloadInvoice(selectedOrder);
      toast.success("Invoice downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // ── Combine and Filter Orders for sidebar ──
  const displayOrders = useMemo(() => {
    let list = [...orders];

    // If selected order was fetched directly via URL and is not in list, prepend it
    if (selectedOrder) {
      const exists = list.some(
        (o) =>
          String(o.order_id ?? o.id) ===
          String(selectedOrder.order_id ?? selectedOrder.id)
      );
      if (!exists) {
        list = [selectedOrder, ...list];
      }
    }

    // Filter by query (order number or product name)
    if (filterQuery.trim()) {
      const q = filterQuery.trim().toLowerCase();
      list = list.filter((o) => {
        const orderNum = String(o.order_number ?? o.id ?? "").toLowerCase();
        const itemMatch = (o.items ?? []).some((item) =>
          String(item.name ?? item.product_name ?? "").toLowerCase().includes(q)
        );
        return orderNum.includes(q) || itemMatch;
      });
    }

    // Filter by status tab
    if (statusFilter === "ACTIVE") {
      list = list.filter((o) => {
        const st = String(o.order_status ?? o.status ?? "").toUpperCase();
        return !["DELIVERED", "CANCELLED"].includes(st);
      });
    } else if (statusFilter === "DELIVERED") {
      list = list.filter(
        (o) =>
          String(o.order_status ?? o.status ?? "").toUpperCase() === "DELIVERED"
      );
    } else if (statusFilter === "CANCELLED") {
      list = list.filter(
        (o) =>
          String(o.order_status ?? o.status ?? "").toUpperCase() === "CANCELLED"
      );
    }

    return list;
  }, [orders, selectedOrder, filterQuery, statusFilter]);

  // ── Order Calculations & Normalized Tracking ──
  const rawStatus = (
    selectedOrder?.order_status ??
    selectedOrder?.status ??
    "PENDING"
  ).toUpperCase();
  const isCancelled = rawStatus === "CANCELLED";
  const isDelivered = rawStatus === "DELIVERED";
  const canCancel = ["PENDING", "ACCEPTED", "CONFIRMED", "PROCESSING"].includes(rawStatus);

  const statusMilestone = STATUS_MILESTONE_STEP[rawStatus] ?? 1;

  // Extract from tracking data
  const trackTimeline = order_tracking?.timeline ?? {};
  const deliveryPartner = order_tracking?.delivery_partner ?? null;
  const etaMinutes = order_tracking?.eta_minutes ?? null;
  const distanceKm = order_tracking?.distance_to_customer ?? null;
  const lastUpdated = order_tracking?.last_updated_at ?? null;

  // Percentage & Status text calculation with rock-solid fallbacks
  const progressPct =
    order_tracking?.progress_percentage != null
      ? order_tracking.progress_percentage
      : (STATUS_PROGRESS_PERCENTAGE[rawStatus] ?? 20);

  const statusText =
    order_tracking?.delivery_status_text ||
    STATUS_DESCRIPTION_TEXT[rawStatus] ||
    "Order in progress";

  // Build 6 Timeline Steps
  const calculatedSteps = TRACKING_STEPS.map((step) => {
    // Check direct tracking timestamp or alternate key
    let timestamp = trackTimeline[step.key] ?? null;
    if (!timestamp && step.altKeys) {
      for (const alt of step.altKeys) {
        if (trackTimeline[alt]) {
          timestamp = trackTimeline[alt];
          break;
        }
      }
    }

    // Fallbacks for order placed or delivered timestamp from order object
    if (!timestamp && step.stepNum === 1) {
      timestamp = selectedOrder?.placed_at || selectedOrder?.created_at || null;
    }
    if (!timestamp && step.stepNum === 6 && isDelivered) {
      timestamp = selectedOrder?.updated_at || selectedOrder?.delivered_at || null;
    }

    const isStepDone =
      isDelivered ||
      Boolean(timestamp) ||
      step.stepNum <= statusMilestone;

    const isStepActive =
      !isCancelled &&
      !isDelivered &&
      !isStepDone &&
      step.stepNum === statusMilestone + 1;

    return {
      ...step,
      timestamp,
      done: isStepDone,
      active: isStepActive,
    };
  });

  // Delivery options & slots
  const slot =
    selectedOrder?.delivery_slot_name ||
    selectedOrder?.slot_name ||
    selectedOrder?.time_slot ||
    selectedOrder?.delivery_slot ||
    "";
  const delType = String(
    selectedOrder?.delivery_type || selectedOrder?.delivery_option || ""
  ).toUpperCase();
  const isScheduled = delType === "SCHEDULED" || Boolean(slot);
  const DelivIcon = isScheduled ? FaCalendarAlt : delType === "EXPRESS" ? FaBolt : FaTruck;

  // Partner Info
  const isPartnerObj = deliveryPartner && typeof deliveryPartner === "object";
  const partnerName = isPartnerObj
    ? deliveryPartner.name ?? deliveryPartner.full_name ?? "Partner Assigned"
    : typeof deliveryPartner === "string"
    ? deliveryPartner
    : null;
  const partnerPhone = isPartnerObj
    ? deliveryPartner.phone ?? deliveryPartner.mobile ?? null
    : null;
  const partnerRating =
    isPartnerObj && deliveryPartner.rating != null
      ? Number(deliveryPartner.rating)
      : null;
  const partnerPhoto =
    isPartnerObj && deliveryPartner.photo_url
      ? buildAssetUrl(deliveryPartner.photo_url)
      : null;
  const partnerVehicle = isPartnerObj
    ? [deliveryPartner.vehicle, deliveryPartner.vehicle_number].filter(Boolean).join(" · ")
    : null;

  const totalPages = orders_pagination?.total_pages ?? 1;

  const copyOrderNumber = () => {
    const num = selectedOrder?.order_number ?? `#${selectedOrderId}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(num);
      toast.success("Order number copied!");
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Track Your Order</h1>
          <p className="mt-1 text-sm text-gray-500">
            Real-time delivery updates, order timeline & shipment breakdown
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing || tracking_loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold text-gray-700 shadow-2xs hover:bg-gray-50 active:scale-95 transition disabled:opacity-50"
          >
            <FaSyncAlt className={`h-3.5 w-3.5 ${isRefreshing || tracking_loading ? "animate-spin text-[#046a38]" : ""}`} />
            <span>{isRefreshing ? "Refreshing..." : "Refresh Status"}</span>
          </button>
        </div>
      </div>

      {/* ── Error Banner if any ── */}
      {orders_error && !orders_loading && (
        <div className="flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800 text-xs">
          <FaTimesCircle className="h-5 w-5 shrink-0 text-rose-500" />
          <div className="flex-1">
            <p className="font-bold">Unable to load orders</p>
            <p className="text-rose-600 mt-0.5">{orders_error}</p>
          </div>
          <button
            onClick={() => fetchOrders({ page: 1, limit: PAGE_LIMIT })}
            className="rounded-xl bg-rose-600 px-3 py-1.5 font-bold text-white hover:bg-rose-700 active:scale-95 transition"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Main Dual-Pane Layout ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[330px_minmax(0,1fr)] xl:grid-cols-[360px_minmax(0,1fr)]">

        {/* ════════════════ LEFT PANE: Order Selector ════════════════ */}
        <div className="flex flex-col rounded-3xl border border-gray-100 bg-white shadow-xs overflow-hidden h-[fit-content] max-h-[820px]">
          {/* Header & Filter Search */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/70 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-gray-700">
                Your Orders
              </span>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-black text-[#046a38]">
                {orders_pagination?.total_orders ?? orders.length}
              </span>
            </div>

            {/* Search Input */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-3 w-3" />
              <input
                type="text"
                placeholder="Search order # or item..."
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-200 rounded-xl focus:border-[#046a38] focus:ring-1 focus:ring-[#046a38] outline-hidden placeholder:text-gray-400"
              />
            </div>

            {/* Quick Status Filter Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-0.5">
              {[
                { id: "ALL", label: "All" },
                { id: "ACTIVE", label: "Active" },
                { id: "DELIVERED", label: "Delivered" },
                { id: "CANCELLED", label: "Cancelled" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setStatusFilter(tab.id)}
                  className={`rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-wider transition ${
                    statusFilter === tab.id
                      ? "bg-[#046a38] text-white shadow-2xs"
                      : "bg-white text-gray-500 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* List of Orders */}
          <div className="overflow-y-auto divide-y divide-gray-50 flex-1 min-h-[220px]">
            {orders_loading && orders.length === 0 ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse space-y-2 p-3 bg-gray-50 rounded-2xl">
                    <div className="h-3 w-28 bg-gray-200 rounded" />
                    <div className="h-2 w-16 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : displayOrders.length > 0 ? (
              displayOrders.map((order) => {
                const id = order.order_id ?? order.id;
                const isSelected = String(id) === String(selectedOrderId);
                return (
                  <OrderListItem
                    key={id}
                    order={order}
                    isSelected={isSelected}
                    onClick={() => handleSelectOrder(order)}
                  />
                );
              })
            ) : (
              <div className="p-8 text-center text-xs text-gray-400">
                <FaBoxOpen className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="font-semibold text-gray-600">No orders found</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {filterQuery ? "Try a different search term" : "Your placed orders will appear here"}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="p-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || orders_loading}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 bg-white font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <FaChevronLeft className="h-2.5 w-2.5" /> Prev
              </button>
              <span className="text-[11px] font-bold text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || orders_loading}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 bg-white font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next <FaChevronRight className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
        </div>

        {/* ════════════════ RIGHT PANE: Order Track Details ════════════════ */}
        <div ref={detailsRef} className="space-y-5 min-w-0">
          {loadingSelectedOrder && !selectedOrder ? (
            <div className="rounded-3xl border border-gray-100 bg-white p-8 shadow-xs space-y-6">
              <div className="animate-pulse space-y-3">
                <div className="h-6 w-48 bg-gray-100 rounded-xl" />
                <div className="h-4 w-32 bg-gray-100 rounded-lg" />
                <div className="h-24 w-full bg-emerald-50/50 rounded-2xl" />
                <div className="h-48 w-full bg-gray-50 rounded-2xl" />
              </div>
            </div>
          ) : selectedOrder ? (
            <>
              {/* ── CARD 1: Header Status Banner ── */}
              <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-xs">
                {/* Top accent bar */}
                <div
                  className={`h-1.5 w-full ${
                    isCancelled
                      ? "bg-rose-500"
                      : isDelivered
                      ? "bg-[#046a38]"
                      : "bg-gradient-to-r from-emerald-500 via-teal-500 to-[#046a38]"
                  }`}
                />

                <div className="p-5 sm:p-6 space-y-5">
                  {/* Top row: Order Number, Date, Status Pill, and Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                          Tracking Order
                        </span>
                        <button
                          type="button"
                          onClick={copyOrderNumber}
                          title="Copy order number"
                          className="text-gray-400 hover:text-gray-600 transition"
                        >
                          <FaCopy className="h-3 w-3" />
                        </button>
                      </div>
                      <h2 className="font-mono text-xl sm:text-2xl font-black text-gray-900 tracking-wide mt-0.5">
                        {selectedOrder.order_number ?? `#${selectedOrderId}`}
                      </h2>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Placed on {fmtDateTime(selectedOrder.placed_at ?? selectedOrder.created_at)}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1 text-xs font-black uppercase tracking-wider ${
                          STATUS_PILL[rawStatus] ?? "bg-gray-100 text-gray-700 border-gray-200"
                        }`}
                      >
                        <span className={`h-2 w-2 rounded-full ${STATUS_DOT[rawStatus] ?? "bg-gray-400"}`} />
                        {ORDER_STATUSES[rawStatus]?.label ?? rawStatus}
                      </span>

                      {/* Download Invoice Button */}
                      <button
                        type="button"
                        onClick={handleDownloadInvoice}
                        disabled={downloadingInvoice}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 shadow-2xs hover:bg-gray-50 active:scale-95 transition disabled:opacity-50"
                      >
                        <FaFileInvoice className="h-3.5 w-3.5 text-emerald-600" />
                        <span>{downloadingInvoice ? "Generating..." : "Invoice"}</span>
                      </button>

                      {/* Cancel Button */}
                      {canCancel && (
                        <button
                          type="button"
                          onClick={() => setShowCancelModal(true)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100 active:scale-95 transition"
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Delivery Mode & Slot Badge */}
                  <div
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl p-3.5 border ${
                      isScheduled
                        ? "border-purple-200 bg-purple-50/70 text-purple-950"
                        : delType === "EXPRESS"
                        ? "border-amber-200 bg-amber-50/70 text-amber-950"
                        : "border-emerald-200 bg-emerald-50/70 text-emerald-950"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-xs font-bold">
                      <DelivIcon className="h-4 w-4 shrink-0 text-[#046a38]" />
                      <span>Delivery Method:</span>
                      <span className="font-extrabold uppercase">
                        {isScheduled ? "Scheduled Delivery" : delType === "EXPRESS" ? "Express Delivery" : "Standard Delivery"}
                      </span>
                    </div>

                    {slot && (
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="font-semibold text-gray-500">Scheduled Slot:</span>
                        <span className="rounded-lg bg-white px-2.5 py-0.5 font-black text-purple-900 border border-purple-200 shadow-2xs">
                          {slot}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ── Progress Bar & Live Status text ── */}
                  {!isCancelled && (
                    <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-[#046a38] animate-ping" />
                          <p className="text-xs font-extrabold text-[#046a38]">{statusText}</p>
                        </div>
                        <span className="font-mono text-xs font-black text-[#046a38]">
                          {progressPct}%
                        </span>
                      </div>

                      <div className="h-2.5 w-full rounded-full bg-emerald-100 overflow-hidden shadow-inner">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-500 to-[#046a38] transition-all duration-700"
                          style={{ width: `${Math.min(100, Math.max(10, progressPct))}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Cancelled Banner */}
                  {isCancelled && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50/80 p-4 flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
                        <FaTimesCircle className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-extrabold text-rose-900">This order has been cancelled</p>
                        <p className="text-xs text-rose-600 mt-0.5">
                          {selectedOrder.clean_notes || "If payment was deducted, your refund will be processed within 2-4 business days."}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── Delivery Partner Highlight Card (if active) ── */}
                  {(deliveryPartner || etaMinutes !== null || distanceKm !== null) && (
                    <div className="rounded-2xl border border-cyan-100 bg-gradient-to-r from-cyan-50/90 to-sky-50/50 p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          {partnerPhoto ? (
                            <img
                              src={partnerPhoto}
                              alt={partnerName ?? "Partner"}
                              className="h-12 w-12 rounded-2xl border-2 border-white object-cover shadow-2xs"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-600 text-white shadow-md shadow-cyan-200">
                              <FaMotorcycle className="h-6 w-6" />
                            </div>
                          )}

                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-black uppercase tracking-wider text-cyan-800">
                                Assigned Rider
                              </span>
                              {partnerRating && (
                                <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-black text-amber-800">
                                  ★ {partnerRating.toFixed(1)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-black text-gray-900 mt-0.5">
                              {partnerName || "Kisan Fresh Delivery Partner"}
                            </p>
                            {partnerVehicle && (
                              <p className="text-xs text-cyan-700 font-medium">{partnerVehicle}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {(etaMinutes !== null || distanceKm !== null) && (
                            <div className="text-right">
                              {etaMinutes !== null && (
                                <p className="text-xs font-bold text-cyan-900">
                                  ETA: <span className="font-black text-sm text-[#046a38]">{etaMinutes} mins</span>
                                </p>
                              )}
                              {distanceKm !== null && (
                                <p className="text-[11px] text-cyan-700 font-medium">
                                  {distanceKm} km away
                                </p>
                              )}
                            </div>
                          )}

                          {partnerPhone && (
                            <a
                              href={`tel:${partnerPhone}`}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-[#046a38] px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-emerald-200 hover:bg-[#035530] active:scale-95 transition"
                            >
                              <FaPhoneAlt className="h-3 w-3" />
                              <span>Call Partner</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Stepper Timeline ── */}
                  {!isCancelled && (
                    <div className="pt-2">
                      <h3 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-4">
                        Live Tracking Timeline
                      </h3>
                      <div className="space-y-1">
                        {calculatedSteps.map((step, idx) => (
                          <TimelineStep
                            key={step.key}
                            step={step}
                            isDone={step.done}
                            isActive={step.active}
                            isLast={idx === calculatedSteps.length - 1}
                          />
                        ))}
                      </div>
                      {lastUpdated && (
                        <p className="text-right text-[11px] text-gray-400 mt-2 font-medium">
                          Last synchronized: {fmtDateTime(lastUpdated)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Card 1 ends */}
            </>
          ) : (
            /* No order selected state */
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-200 bg-white p-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-50 text-[#046a38] mb-4">
                <FaTruck className="h-7 w-7" />
              </div>
              <h3 className="text-base font-extrabold text-gray-800">Select an order to track</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-sm">
                Choose any order from the left list or search by order number to see real-time updates and delivery status.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Cancel Confirmation Modal ── */}
      {showCancelModal && selectedOrder && (
        <CancelModal
          order={selectedOrder}
          loading={cancelling}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
};

export default TrackOrderSection;
